import { css, keyframes, styled } from 'goober';

// widget
export const AgentToggleBtn = styled('button')(
    (props: { isOpen: boolean; position: 'left' | 'right' }) => `
    position: fixed;
    bottom: 1rem;
    ${props.position}: 20px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: none;
    z-index: 9999;
    background-color: var(--bb-primary);
    
    /* Mobile optimizations - prevent double tap */
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    touch-action: manipulation;
    transition: transform 0.1s ease;
    
    &:active {
        transform: scale(0.95);
    }
`
);

export const AgentWapper = styled('div')`
    position: relative;
    z-index: 9999;
`;

export const ChatbotWrapper = css`
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #00000080;
    animation: fadeIn 0.3s ease-out 0.2s forwards;
    opacity: 0;

    @keyframes fadeIn {
        to {
            opacity: 1;
        }
    }
`;

export const AgentChatbotWindow = styled('div')<{
    position: 'left' | 'right';
}>`
    position: fixed;
    ${(props) => `${props.position}: 20px;`}
    bottom: 90px;
    border: 1px solid var(--bb-border-color);
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 9999;
    right: 20px;

    @media only screen and (max-width: 640px) {
        width: 90% !important;
    }
`;

export const AgentChatbotWindowExpand = css`
    animation: slideInAndExpand 0.4s ease-out 0.3s forwards;

    @keyframes slideInAndExpand {
        to {
            transform: translateX(0) scale(1);
            opacity: 1;
        }
    }
`;

// Chabot window
export const StyleVariables = (
    render: 'wc' | 'rc' = 'wc',
    pColor: string = '#3387d7',
    headerBgColor: string = '#3387d7',
    borderRadius: string = '14',
    chatbotWidth: string = '400'
) => {
    return `
        ${render === 'rc' ? ':root' : ':host'} {
            --bb-font-body: 0.875rem;
            --bb-font-title: 1.125rem;
            --bb-text-color: #030712;
            --bb-border-color: #e5e7eb;
            --bb-primary: ${pColor};
            --bb-background: 0 0% 100%;
            --bb-header-background: ${headerBgColor};
            --bb-header-text: ${headerBgColor.toLowerCase() === '#ffffff' ? '#030712' : '#ffffff'};
            --bb-border-radius: ${borderRadius}px;
            --bb-popup-width: ${chatbotWidth}px;

            display: block;
            background-color: var(--bb-background);
            color: hsl(var(--bb-text-color));
            font-size: var(--td-font-body);
            line-height: normal;
            font-weight: 400;
            
            /* Global mobile optimizations - ensure single-tap works */
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
            -ms-touch-action: manipulation;
            touch-action: manipulation;
        }

       ${
           render === 'rc'
               ? `.bb-ai-agent,.bb-ai-agent:before,.bb-ai-agent:after`
               : '*,*:before,*:after'
       } {
            font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji;
            box-sizing: border-box;
            font-size: var(--bb-font-body);
            line-height: 1.5;
            margin: 0;
            padding: 0;
        }
        
        /* Ensure all interactive elements work with single tap on mobile */
        ${render === 'rc' ? '.bb-ai-agent' : ':host'} button,
        ${render === 'rc' ? '.bb-ai-agent' : ':host'} a,
        ${render === 'rc' ? '.bb-ai-agent' : ':host'} [onclick],
        ${render === 'rc' ? '.bb-ai-agent' : ':host'} [role="button"] {
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            user-select: none;
            touch-action: manipulation;
            cursor: pointer;
        }
    `;
};

export const ChatbotContainer = styled('div')`
    width: 100%;
    overflow: hidden;
    height: 80vh;
    display: flex;
    flex-direction: column;

    @media only screen and (max-width: 640px) {
        width: 100%;
    }
`;

export const ChatbotHeader = styled('div')`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--bb-border-color);
    background: var(--bb-header-background);
    min-height: 60px;

    .b-agent__brand-name {
        font-size: var(--bb-font-title);
        font-weight: 600;
        color: var(--bb-header-text);
    }

    .b-agent__header-brand {
        display: flex;
        align-items: center;
        gap: 10px;
    }
`;

export const ChatbotHeaderLogo = css`
    --size: 40px;
    width: var(--size);
    height: var(--size);
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    color: #ffffff;
    border-radius: 10px;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

export const ChatbotHeaderAction = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    color: var(--bb-header-text);

    button {
        cursor: pointer;
        border: none;
        outline: none;
        background: transparent;
        color: var(--bb-header-text);
        padding: 0;
        margin: 0;

        &:disabled {
            cursor: not-allowed;
        }
    }
`;

export const MessagesWrapper = styled('div')`
    padding: 1rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: auto;
`;

export const IntroMessage = styled('div')`
    font-size: var(--bb-font-body);
    margin-bottom: 6px;
    white-space: pre-wrap;
    line-height: 1.5;
`;

export const MessagesItemHuman = css(`
    display: block;
    padding: 0.35rem 0.6rem;
    font-size: var(--bb-font-body);
    color: #ffffff;
    background: var(--bb-primary);
    border-radius: 8px;
    max-width: 90%;
    min-width: min-content;
    margin-left: auto;
`);

export const MessagesItemAssistant = css(`
    display: block;
    padding: 0.35rem 0.6rem;
    font-size: var(--bb-font-body);
    color: var(--bb-text-color);
    background: #f5f5f5;
    border-radius: 8px;
    width: 100%;
    margin-right: auto;

    pre {
        overflow: auto;
    }

    ::-webkit-scrollbar {
        -webkit-appearance: none;
    }

    ::-webkit-scrollbar:vertical {
        width: 12px;
    }

    ::-webkit-scrollbar:horizontal {
        height: 9px;
    }

    ::-webkit-scrollbar-thumb {
        background-color: var(--bb-primary);
        border-radius: 10px;
        border: 2px solid #ffffff;
    }

    ::-webkit-scrollbar-track {
        border-radius: 10px;
        background-color: #f1f5f9;
    }

    table {
        width: max-content;
        max-width: 100%;
        margin: 10px 0;
        display: block;
        border-spacing: 0;
        overflow: auto;
        border-collapse: collapse;
        vertical-align: middle;

        th, td {
            border: 1px solid var(--bb-border-color);
            padding: 6px;
            white-space: nowrap;
            text-align: start;
            color: var(--bb-text-color);
        }

        span {
            display: inline-flex;
            align-items: center;
            column-gap: 5px;
        }
    }

    a {
        color: var(--bb-primary);
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 4px;

        svg {
            --size: 14px;
            width: var(--size);
            height: var(--size);
            fill: var(--bb-primary);
        }
    }

    p + p {
        margin-bottom: 6px;
    }

    ul, ol {
        margin-left: 20px;
        
        li {
            margin-bottom: 6px;
        }
    }

    img {
        width: 100%;
    }
`);

export const MessageItemStarting = css`
    border-radius: 8px;
    border: 1px solid var(--bb-border-color);
    cursor: pointer;
    color: var(--bb-text-color);
    background: #f5f5f5;
    display: block;
    padding: 0.3rem 0.6rem;
    font-size: var(--bb-font-body);
    max-width: 90%;
    min-width: max-content;
    margin-right: auto;
    text-align: left;

    /* Mobile optimizations - prevent double tap */
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    touch-action: manipulation;
    transition: background-color 0.1s ease;

    &:active {
        background: #e5e5e5;
    }

    /* Only enable hover on non-touch devices */
    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background: #e5e5e5;
        }
    }
`;

export const MessageForm = styled('form')`
    display: flex;
    align-items: end;
    gap: 0 14px;
    padding: 1rem;
    border-top: 1px solid var(--bb-border-color);
`;

export const MessageFormInput = css`
    flex-grow: 1;
    padding: 0.75rem 1rem;
    border: 1px solid var(--bb-border-color);
    border-radius: 10px;
    font-size: var(--bb-font-body);
    outline: none;
    resize: none;
    line-height: 1.25;
    min-height: 45px;

    &:focus {
        border-color: var(--bb-primary);
    }
`;

export const MessageFormBtn = styled('button')`
    --size: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--size);
    height: var(--size);
    background-color: var(--bb-primary);
    color: #ffffff;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition:
        background-color 0.1s ease,
        transform 0.1s ease;

    /* Mobile optimizations - prevent double tap */
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    touch-action: manipulation;

    &:active {
        transform: scale(0.95);
    }

    &:disabled {
        background-color: var(--bb-primary);
        color: #d1d5db;
        cursor: not-allowed;
        opacity: 0.6;
        transform: none;
    }
`;

export const ChatbotFooter = styled('p')`
    font-size: 0.75rem;
    text-align: center;
    margin-bottom: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
    color: var(--bb-text-color);

    img {
        width: 80px;
        height: auto;
    }
`;

export const dialogWrapper = css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: rgb(0 0 0 / 50%);
    width: 100%;
    height: 100%;
    justify-content: center;
    display: flex;
    align-items: center;
    z-index: 999;
`;

export const dialog = css`
    background: white;
    border-radius: 8px;
    box-shadow:
        0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 10px 10px -5px rgba(0, 0, 0, 0.04);
    width: 80%;
    max-width: 350px;
    padding: 25px 20px;
    position: relative;
`;

export const dialogTitle = css`
    font-size: 20px;
    font-weight: 500;
    color: var(--bb-text-color);
    text-align: center;
    margin-bottom: 12px;
    line-height: 1.3;
`;

export const dialogDescription = css`
    font-size: 14px;
    color: #6b7280;
    text-align: center;
    line-height: 1.5;
    margin-bottom: 20px;
`;

export const dialogActions = css`
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
`;

export const button = css`
    padding: 4px 16px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    text-align: center;
    min-width: 90px;

    &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.1);
    }
`;

export const buttonSecondary = css`
    background: white;
    color: var(--bb-text-color);
    border: 1px solid var(--bb-border-color);
`;

export const buttonPrimary = css`
    background: var(--bb-primary);
    color: #fff;
    border: 1px solid var(--bb-primary);
`;

export const typingIndicator = css`
    display: flex;
    align-items: center;
`;

// Pulse animation keyframes
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  45% {
    transform: scale(0.1);
    opacity: 0.7;
  }
  80% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Loader dot style
const getPulseDot = (delay = '0s') => css`
    background-color: #9d9d9d;
    width: 12px;
    height: 12px;
    margin: 2px;
    border-radius: 100%;
    display: inline-block;
    animation: ${pulse} 0.75s cubic-bezier(0.2, 0.68, 0.18, 1.08) infinite;
    animation-delay: ${delay};
`;

// Use specific delays
export const pulseDot1 = getPulseDot('0s');
export const pulseDot2 = getPulseDot('0.12s');
export const pulseDot3 = getPulseDot('0.24s');

export const buttonStyle = css`
    border: none;
    background: none;
    padding: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }
`;

export const chatContainer = css`
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
    font-family:
        system-ui,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        Roboto,
        Oxygen,
        Ubuntu,
        Cantarell,
        'Open Sans',
        'Helvetica Neue',
        sans-serif;
`;

export const chatButton = css`
    border: none;
    background: var(--bb-primary, #2e0a57);
    color: white;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
`;

export const sendButton = css`
    border: none;
    background: var(--bb-primary, #2e0a57);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #1a0635;
    }

    &:active {
        background-color: #110422;
    }
`;
